import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Package } from 'lucide-react';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useEntitlements } from '../../hooks/useEntitlements';
import type { Product } from '../../types';

export default function Products() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { entitlements, hasAccess } = useEntitlements();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsQuery = query(
          collection(db, 'products'),
          where('active', '==', true),
          orderBy('priceZAR', 'asc')
        );
        const snapshot = await getDocs(productsQuery);
        const productsData: Product[] = [];

        snapshot.forEach(doc => {
          const product = { id: doc.id, ...doc.data() } as Product;
          // Filter out lead-magnet from catalog (user already has it)
          if (product.id !== 'lead-magnet') {
            productsData.push(product);
          }
        });

        setProducts(productsData);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Determine suggested next product based on what user owns
  const getSuggestedProduct = () => {
    if (hasAccess('tripwire') && !hasAccess('premium')) return 'premium';
    if (hasAccess('premium') && !hasAccess('audit')) return 'audit';
    if (!hasAccess('tripwire')) return 'tripwire';
    return null;
  };

  const suggestedProductId = getSuggestedProduct();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Unlock More Business Tools</h1>
        <p className="mt-2 text-gray-600">
          Grow your business with the right legal documents at each stage
        </p>
      </div>

      {/* Products owned summary */}
      {entitlements.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-800">
            <strong>You own {entitlements.length} {entitlements.length === 1 ? 'product' : 'products'}</strong> • Keep growing with the next stage
          </p>
        </div>
      )}

      {/* Products grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {products.map((product) => {
          const owned = hasAccess(product.id);
          const isRecommended = product.id === suggestedProductId;

          return (
            <div
              key={product.id}
              className={`bg-white rounded-lg shadow-sm border-2 transition-all hover:shadow-md ${
                isRecommended
                  ? 'border-blue-500 ring-2 ring-blue-200'
                  : owned
                  ? 'border-green-500'
                  : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              {/* Recommended badge */}
              {isRecommended && !owned && (
                <div className="bg-blue-500 text-white text-xs font-bold px-4 py-2 rounded-t-lg">
                  ⭐ RECOMMENDED NEXT STEP
                </div>
              )}

              <div className="p-6">
                {/* Product header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900">{product.name}</h2>
                    <p className="mt-1 text-xl font-semibold text-blue-600">
                      R{product.priceZAR.toLocaleString()}
                    </p>
                  </div>
                  {owned && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      Owned
                    </span>
                  )}
                </div>

                {/* Description */}
                {product.description && (
                  <p className="text-gray-700 mb-4">{product.description}</p>
                )}

                {/* What's included */}
                {product.features && product.features.length > 0 && (
                  <div className="mb-6">
                    <p className="text-sm font-semibold text-gray-900 mb-2">What's included:</p>
                    <ul className="space-y-1">
                      {product.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                      {product.features.length > 3 && (
                        <li className="text-sm text-gray-500 ml-5">
                          + {product.features.length - 3} more items
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {/* CTA Button */}
                <div>
                  {owned ? (
                    <Link
                      to="/portal/downloads"
                      className="block w-full text-center px-4 py-3 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
                    >
                      ✓ View Your Downloads
                    </Link>
                  ) : (
                    <Link
                      to={`/portal/products/${product.id}`}
                      className="block w-full text-center px-4 py-3 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      Get This Pack - R{product.priceZAR.toLocaleString()}
                    </Link>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty state */}
      {products.length === 0 && !loading && (
        <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-medium text-gray-900 mb-2">
            No products available
          </h3>
          <p className="text-gray-600">
            Check back soon for new legal packs and templates
          </p>
        </div>
      )}

      {/* Help text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>💡 Tip:</strong> Follow the Business Journey stages in order to ensure you have the legal foundation your business needs at each growth phase.
        </p>
      </div>
    </div>
  );
}
